import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Mail, X, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function EmailVerificationBanner() {
  const { user, isEmailVerified, resendVerificationEmail } = useAuth();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [sent, setSent] = useState(false);

  // Don't show if verified, dismissed, or no user
  if (!user || isEmailVerified || dismissed) {
    return null;
  }

  const handleResend = async () => {
    setSending(true);
    try {
      await resendVerificationEmail();
      setSent(true);
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200" data-testid="email-verification-banner">
      <div className="container-main py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Mail className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-amber-800">
                <strong>Please verify your email address.</strong>{' '}
                We sent a verification link to <span className="font-medium">{user.email}</span>.
                {' '}Check your inbox and spam folder.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {sent ? (
              <span className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                Sent!
              </span>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResend}
                disabled={sending}
                className="text-amber-700 border-amber-300 hover:bg-amber-100"
                data-testid="resend-verification-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend Email'
                )}
              </Button>
            )}
            <button
              onClick={() => setDismissed(true)}
              className="p-1 text-amber-600 hover:text-amber-800 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
